import React, { createContext, useContext, useEffect, useRef, ReactNode } from 'react';

interface UserData {
    em?: string;
    fn?: string;
    ln?: string;
    external_id?: string;
}

interface GTMContextType {
    trackEvent: (eventName: string, eventData?: Record<string, any>) => void;
    trackPageView: (pagePath: string, pageTitle?: string) => void;
    trackLead: (leadData?: Record<string, any>) => void;
    trackPurchase: (purchaseData: {
        transactionId: string;
        value: number;
        currency?: string;
        items?: any[];
        user_data?: UserData;
        [key: string]: any;
    }) => void;
    setUserId: (userId: string) => void;
    setUserProperties: (properties: Record<string, any>) => void;
}

const GTMContext = createContext<GTMContextType | undefined>(undefined);

interface GTMProviderProps {
    gtmId: string;
    children: ReactNode;
}

declare global {
    interface Window {
        dataLayer: any[];
    }
}

// Generate unique event ID for deduplication
const generateEventId = (prefix: string, transactionId?: string): string => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return transactionId
        ? `${prefix}_${transactionId}_${timestamp}`
        : `${prefix}_${timestamp}_${random}`;
};

export const GTMProvider: React.FC<GTMProviderProps> = ({ gtmId, children }) => {
    const injectedRef = useRef(false);

    useEffect(() => {
        // Initialize dataLayer immediately so events are queued
        window.dataLayer = window.dataLayer || [];

        const injectGTM = () => {
            if (injectedRef.current) return;
            injectedRef.current = true;

            const script = document.createElement('script');
            script.innerHTML = `
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${gtmId}');
            `;
            document.head.appendChild(script);

            // Add noscript iframe
            const noscript = document.createElement('noscript');
            const iframe = document.createElement('iframe');
            iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
            iframe.height = '0';
            iframe.width = '0';
            iframe.style.display = 'none';
            iframe.style.visibility = 'hidden';
            noscript.appendChild(iframe);
            document.body.insertBefore(noscript, document.body.firstChild);
        };

        // Delay GTM injection by 8s or first user interaction (with yield)
        // GTM is the heaviest third-party script (~1,100ms CPU) and loads sub-scripts (GA4, TikTok)
        const timer = setTimeout(injectGTM, 8000);
        const events = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const;
        const onInteraction = () => {
            clearTimeout(timer);
            // Yield to browser before loading so interaction stays responsive
            setTimeout(injectGTM, 200);
            events.forEach(e => document.removeEventListener(e, onInteraction));
        };
        events.forEach(e => document.addEventListener(e, onInteraction, { once: true, passive: true }));

        return () => {
            clearTimeout(timer);
            events.forEach(e => document.removeEventListener(e, onInteraction));
        };
    }, [gtmId]);

    const trackEvent = (eventName: string, eventData?: Record<string, any>) => {
        console.log('GTM Event:', eventName, eventData);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: eventName,
            ...eventData,
        });
    };

    const trackPageView = (pagePath: string, pageTitle?: string) => {
        console.log('GTM Page View:', pagePath);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'pageview',
            page: {
                path: pagePath,
                title: pageTitle || document.title,
            },
        });
    };

    const trackLead = (leadData?: Record<string, any>) => {
        console.log('GTM Lead:', leadData);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'lead',
            event_id: generateEventId('lead'),
            ...leadData
        });
    };

    const trackPurchase = (purchaseData: {
        transactionId: string;
        value: number;
        currency?: string;
        items?: any[];
        user_data?: UserData;
        fbc?: string | null;
        fbp?: string | null;
        [key: string]: any;
    }) => {
        console.log('GTM Purchase:', purchaseData);
        window.dataLayer = window.dataLayer || [];

        // Unique event_id for Pixel + CAPI deduplication
        const eventId = generateEventId('purchase', purchaseData.transactionId);

        window.dataLayer.push({
            event: 'purchase',
            event_id: eventId,
            ecommerce: {
                transaction_id: purchaseData.transactionId,
                value: purchaseData.value,
                currency: purchaseData.currency || 'USD',
                items: purchaseData.items || [],
            },
            user_data: purchaseData.user_data ? {
                em: purchaseData.user_data.em?.toLowerCase().trim(),
                fn: purchaseData.user_data.fn?.toLowerCase().trim(),
                ln: purchaseData.user_data.ln?.toLowerCase().trim(),
                external_id: purchaseData.user_data.external_id,
            } : undefined,
            // Facebook Click ID & Browser ID for Meta CAPI matching
            ...(purchaseData.fbc && { fbc: purchaseData.fbc }),
            ...(purchaseData.fbp && { fbp: purchaseData.fbp }),
        });
    };

    const setUserId = (userId: string) => {
        console.log('GTM Set User ID:', userId);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'user_id_set',
            userId: userId,
        });
    };

    const setUserProperties = (properties: Record<string, any>) => {
        console.log('GTM Set User Properties:', properties);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'user_properties_set',
            userProperties: properties,
        });
    };

    return (
        <GTMContext.Provider
            value={{
                trackEvent,
                trackPageView,
                trackLead,
                trackPurchase,
                setUserId,
                setUserProperties,
            }}
        >
            {children}
        </GTMContext.Provider>
    );
};

// Custom hook to use GTM
export const useGTM = (): GTMContextType => {
    const context = useContext(GTMContext);
    if (!context) {
        throw new Error('useGTM must be used within GTMProvider');
    }
    return context;
};
